/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { create } from "zustand";
import type {
  FilterDataTypes,
  FilterFieldTypes,
  FilterOperationMetadata,
} from '@shared/types';
import { filterFieldsMap } from "./filter-fields-map";

type ColumnId = string;
export type Filters = Record<ColumnId, Filter>;
export type Filter = {
  columnId: ColumnId;
  columnLabel: string;
  filterDataType: FilterDataTypes;
  operationType: FilterOperationMetadata["operationType"];
  fieldValues: Partial<Record<FilterFieldTypes, any>>;
};

export type FiltersStore = {
  filters: Filters;
  setFilters: (filters: Filters) => void;
  removeFilters: (filterKeys: ColumnId | ColumnId[]) => void;
  getUrlFilters: () => Record<string, string>;
};

export const useFiltersStore = create<FiltersStore>((set, get) => ({
  filters: {},
/**
 * setFilters function.
 *
 * @param {*} filters - Parameter description
 *
 * @returns {*} Return value description
 */
  setFilters: (filters) =>
    set((state) => ({ filters: { ...state.filters, ...filters } })),
/**
 * removeFilters function.
 *
 * @param {*} filterKeys - Parameter description
 *
 * @returns {*} Return value description
 */
  removeFilters: (filterKeys) => {
    if (typeof filterKeys === "string") filterKeys = [filterKeys];
    set((state) => {
      const updatedFilters = Object.entries(state.filters).filter(
        ([key, value]) => !filterKeys.includes(key)
      );
      return { filters: Object.fromEntries(updatedFilters) };
    });
  },
/**
 * getUrlFilters function.
 *
 * @returns {*} Return value description
 */
  getUrlFilters: () => {
    const filters = get().filters;
    const urlParams: Record<string, string> = {};
    for (const filter of Object.values(filters)) {
      appendFilterParams(urlParams, filter);
    }
    return urlParams;
  },
}));

function appendFilterParams(params: Record<string, string>, filter: Filter): void {
  for (const [fieldType, value] of Object.entries(filter.fieldValues)) {
    const fieldDefinition = filterFieldsMap[fieldType as FilterFieldTypes];
    const suffix = fieldDefinition.urlOperator ? `_${fieldDefinition.urlOperator}` : '';
    params[`${filter.columnId}${suffix}`] = value;
  }
}

/**
 * useCurrentFilter function.
 *
 * @param {*} columnId - Parameter description
 *
 * @returns {*} Return value description
 */
export const useCurrentFilter = (columnId: string): Filter | undefined =>
  useFiltersStore((state) => state.filters[columnId]);
